import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface SwipeHintProps {
  /** Which edge the hint is revealed from: `left` = the card is dragged right. */
  side: 'left' | 'right';
  /** Gesture progress, 0 to 1. Drives the fade and scale. */
  progress: number;
  tone: 'success' | 'danger';
  label: string;
  icon: ReactNode;
  /** Edge padding for the icon (default `5`, i.e. 1.25rem). */
  inset?: 4 | 5;
  /** Let pointer events fall through to the card above. */
  passThrough?: boolean;
}

/** The coloured reveal behind a swipeable workspace card (Tasks, Meetings). */
export function SwipeHint({ side, progress, tone, label, icon, inset = 5, passThrough = false }: SwipeHintProps) {
  const edge = side === 'left' ? (inset === 4 ? 'justify-start pl-4' : 'justify-start pl-5') : inset === 4 ? 'justify-end pr-4' : 'justify-end pr-5';
  return (
    <div
      className={cn(
        'absolute inset-0 flex items-center rounded-2xl text-white',
        tone === 'success' ? 'bg-success' : 'bg-danger',
        edge,
        passThrough && 'pointer-events-none',
      )}
      style={{ opacity: progress > 0 ? 1 : 0 }}
    >
      <div
        className={cn('flex flex-col items-center gap-1', side === 'left' ? 'origin-left' : 'origin-right')}
        style={{ opacity: progress, transform: `scale(${0.7 + 0.3 * progress})` }}
      >
        {icon}
        <span className="text-micro font-bold leading-none">{label}</span>
      </div>
    </div>
  );
}

/** Small expand/collapse chevron that flips when `open`. */
export function ExpandChevron({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      className={cn('transition-transform duration-200', open && 'rotate-180', className)}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
